import i18n from "@/app/i18n";
import { type Building, FIND_MY_CAR_LOCATION_ID } from "@/libs/models";
import { MeasurementSystems } from "../app/constants";
import type { Action, SupportedLanguage } from "../config/types";
import type { RouteIndication, RouteStep } from "../navigation/types";
import { translateIndicationMessage } from "./utils";

export enum OrientationTypesSdk {
  BACKWARD = "BACKWARD", //NOTE: this is to maintain compatibility with SDKs
  LEFT = "LEFT",
  RIGHT = "RIGHT",
  AROUND = "AROUND",
}

export enum IndicationType {
  INITIAL = "INITIAL",
  CHANGE_LEVEL = "CHANGE_LEVEL",
  CHANGE_FLOOR = "CHANGE_FLOOR",
  PASS_THROUGH = "PASS_THROUGH",
  WALK = "WALK",
  GO_AHEAD = "GO_AHEAD",
  TURN = "TURN",
  DESTINATION_REACHED = "DESTINATION_REACHED",
  END = "END",
  LOOK_AT = "LOOK_AT",
  TURN_SLIGHT = "TURN_SLIGHT",
}

/**
 * Obtain poiId from indications step metadata
 * @param metaData
 * @returns
 */
export const getPoiIdFromIndicationMetaData = (metaData: string[]) => {
  return metaData
    ?.map((item) => {
      try {
        const parsedItem = JSON.parse(item) as any;
        return parseInt(parsedItem?.poiId ?? 0);
      } catch {
        return null;
      }
    })
    .find(Boolean);
};

export const getRelatedPoiNameForIndication = (
  indication: RouteIndication,
  building: Building,
  destinationId: number,
) => {
  if (
    indication.indicationType == IndicationType.END ||
    indication.indicationType == IndicationType.DESTINATION_REACHED
  ) {
    if (destinationId == FIND_MY_CAR_LOCATION_ID) {
      return i18n.t("find_my_car.name");
    } else {
      return building?.pois
        ?.getPoiById(destinationId ?? -1)
        ?.getTranslatedProperty("name");
    }
  } else {
    return building?.pois
      ?.getPoiById(getPoiIdFromIndicationMetaData(indication?.metaData) ?? -1)
      ?.getTranslatedProperty("name");
  }
};

export const getFloorFromIndication = (
  indication: RouteIndication,
  building?: Building,
) => {
  if (!building) return;
  return building.getFloorById(indication?.floorIdentifier);
};

export const getDestinationFloorFromIndication = (
  indication: RouteIndication,
  building?: Building,
) => {
  if (!building) return;
  return building.getFloorById(indication?.nextFloorIdentifier);
};

export const getHumanReadableIndication = ({
  indication,
  steps,
  currentBuilding,
  destinationId,
  nextIndication,
  metadata: {
    actions,
    lang,
    source = "directions",
    currentMeasurementSystem = MeasurementSystems.metric,
  },
}: {
  indication: RouteIndication | null;
  steps: RouteStep[];
  currentBuilding: Building | null;
  destinationId: number;
  nextIndication?: RouteIndication | null;
  metadata?: {
    actions?: Action[];
    lang?: SupportedLanguage;
    source?: "navigation" | "directions";
    currentMeasurementSystem?: MeasurementSystems;
  };
}): string | null => {
  if (!indication) return null;
  const originFloorId =
    steps[indication?.stepIdxOrigin - 1]?.from?.floorIdentifier;

  const destinationFloor = getDestinationFloorFromIndication(
    indication,
    currentBuilding,
  );
  const originFloor = currentBuilding?.getFloorById(parseInt(originFloorId));
  const relatedPoiName = getRelatedPoiNameForIndication(
    indication,
    currentBuilding,
    destinationId,
  );

  return translateIndicationMessage({
    floorName: destinationFloor?.getPrintName() || originFloor?.getPrintName(),
    indication,
    metadata: {
      actions,
      currentMeasurementSystem,
      lang,
      source,
    },
    nextIndication,
    relatedPoiName,
  });
};
